// Headline benefits for a plan row in the quote wizard (Step 5 Plan Selection:
// the Ded / Office / ER / Rx / OOP columns, the Selected Plans sidebar and the
// current-vs-renewal table).
//
// Plans arrive with `Benefits` from GET /api/plan-source/plans (and
// plan-details): plans.raw_benefits for library plans, legacy plan_benefits,
// or the W&B cache. The reading itself lives in @shared/utils/planBenefitLines
// so the proposal page and PDF read the same lines the same way (DEV-355).
import { summarizeBenefitLines } from "@shared/utils/planBenefitLines";
import { isSamePlan } from "./planIdentity";

export interface BenefitsSummary {
  deductible?: string;
  officeVisit?: string;
  er?: string;
  rxTier1?: string;
  rxTier2?: string;
  oopMax?: string;
}

function planBenefitLines(plan: any): unknown {
  return plan?.Benefits ?? plan?.benefits ?? [];
}

export function extractBenefitsSummary(plan: any): BenefitsSummary {
  const s = summarizeBenefitLines(planBenefitLines(plan));
  const out: BenefitsSummary = {};
  if (s.deductible) out.deductible = s.deductible;
  if (s.copay) out.officeVisit = s.copay;
  if (s.emergencyRoom) out.er = s.emergencyRoom;
  if (s.rxGeneric) out.rxTier1 = s.rxGeneric;
  if (s.rxBrand) out.rxTier2 = s.rxBrand;
  if (s.outOfPocketMax) out.oopMax = s.outOfPocketMax;
  return out;
}

const hasLines = (raw: unknown) =>
  Array.isArray(raw) ? raw.length > 0 : !!raw && typeof raw === "object" && Object.keys(raw).length > 0;

// A selected plan is a copy made when it was picked. Quotes saved before the
// plans endpoint served benefits hold copies without them, so borrow the
// benefits of the same plan as currently listed.
export function withListedBenefits(plan: any, listed: any[] | undefined): any {
  if (!plan || hasLines(planBenefitLines(plan))) return plan;
  const match = (listed || []).find((p) => isSamePlan(p, plan) && hasLines(planBenefitLines(p)));
  return match ? { ...plan, Benefits: planBenefitLines(match) } : plan;
}

// Which benefits a Step 5 row shows: the plan's own (served by the library,
// authoritative), else the same plan as listed, else the rates-cache entry
// (plan-details prefetch / /api/plan-cache rows, which can be stale or empty).
export function withPlanBenefits(plan: any, cachedBenefits: unknown, listed?: any[]): any {
  const own = withListedBenefits(plan, listed);
  if (!own || hasLines(planBenefitLines(own))) return own;
  return hasLines(cachedBenefits) ? { ...own, Benefits: cachedBenefits } : own;
}
